import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import NotificationCard from "../components/NotificationCard";

interface NotificationWithTrip {
  id: string;
  title: string;
  message: string;
  importance: "normal" | "important";
  created_at: string;
  trip_id: string;
  user_id: string;
  read_by: string[] | null;
  trips: {
    name: string;
    creator_id: string;
  } | null;
  profiles: {
    first_name: string;
    last_name: string;
  } | null;
}

export default function NotificationPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationWithTrip[]>([]);
  const [loading, setLoading] = useState(true);

  // 👇 Hilfsfunktion: alle Trip-IDs, für die der User Zugriff hat
  const fetchAccessibleTripIds = async (userId: string) => {
    // 1) User ist Teilnehmer (nicht geblockt)
    const { data: memberRows, error: memberErr } = await supabase
      .from("trip_participants")
      .select("trip_id")
      .eq("user_id", userId)
      .eq("is_blocked", false);

    if (memberErr) throw memberErr;

    // 2) User ist Ersteller
    const { data: creatorRows, error: creatorErr } = await supabase
      .from("trips")
      .select("id")
      .eq("creator_id", userId);

    if (creatorErr) throw creatorErr;

    const ids = new Set<string>();
    (memberRows ?? []).forEach((r: any) => r?.trip_id && ids.add(r.trip_id));
    (creatorRows ?? []).forEach((r: any) => r?.id && ids.add(r.id));

    return Array.from(ids);
  };

  // 🔁 GET notifications gefiltert nach zugänglichen Trips
  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const tripIds = await fetchAccessibleTripIds(user.id);

      // Wenn keine Trips → leere Liste
      if (!tripIds.length) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      // Notifications nur für diese Trips
      const { data, error } = await supabase
        .from("notifications")
        .select("*, trips(name, creator_id), profiles(first_name, last_name)")
        .in("trip_id", tripIds)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Fehler beim Laden:", error.message);
        setNotifications([]);
        setLoading(false);
        return;
      }

      const prepared = (data as NotificationWithTrip[]).map((n) => ({
        ...n,
        // Falls Supabase die Joins als Array liefert
        trips: Array.isArray(n.trips) ? n.trips[0] : n.trips,
        profiles: Array.isArray(n.profiles) ? n.profiles[0] : n.profiles,
        read_by: n.read_by ?? [], // defensive
      }));

      const sorted = prepared.sort((a, b) => {
        const aImportant = a.importance === "important";
        const bImportant = b.importance === "important";
        const aRead = (a.read_by ?? []).includes(user.id);
        const bRead = (b.read_by ?? []).includes(user.id);

        // Wichtigkeit (wichtig vor normal)
        if (aImportant !== bImportant) return aImportant ? -1 : 1;
        // Ungelesen vor gelesen
        if (aRead !== bRead) return aRead ? 1 : -1;
        // Dann Datum absteigend
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      setNotifications(sorted);
    } catch (e: any) {
      console.error("❌ Ladefehler:", e.message ?? e);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (error) {
      console.error("❌ Fehler beim Löschen:", error.message);
    } else {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    }
  };

  // 🔁 Erstladung bei user.id
  useEffect(() => {
    if (user?.id) loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 🔁 Realtime bei INSERT → einfach neu laden (Filter greift in loadNotifications)
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("notifications-page")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ✅ Als gelesen markieren
  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return;

    const { error } = await supabase.rpc("mark_notification_as_read", {
      notif_id: notificationId,
      reader_id: user.id,
    });

    if (error) {
      console.error("❌ Fehler beim Aktualisieren:", error.message);
    } else {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, read_by: [...(n.read_by ?? []), user.id] }
            : n
        )
      );
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        Lade Benachrichtigungen...
      </div>
    );

  if (notifications.length === 0)
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className="text-2xl font-bold text-slate-100">
              📭 Dein Postfach ist leer 👌
            </h1>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-20 px-4 sm:px-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-100">
            🔔 Deine Benachrichtigungen
          </h1>
        </div>

        {notifications.length === 0 && (
          <div className="bg-[#161b27] border border-[#2a3347] p-6 rounded-xl text-center text-slate-400">
            📭 Keine Benachrichtigungen vorhanden.
          </div>
        )}

        {notifications.map((n) => {
          const isOrga = !!(n.trip_id && n.trips?.creator_id === user?.id);

          return (
            <NotificationCard
              key={n.id}
              id={n.id}
              title={n.title}
              message={n.message}
              importance={n.importance}
              created_at={n.created_at}
              isRead={(n.read_by ?? []).includes(user!.id)}
              tripName={n.trips?.name ?? ""}
              tripId={n.trip_id}
              author={n.profiles ?? undefined}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              isOrga={isOrga}
            />
          );
        })}
      </div>
    </div>
  );
}
